import { Injectable } from '@angular/core';
import { AuthorizationServiceInterface } from './authorizationService.interface';
import { EventsHubService } from '../events-hub.service';
import { AuthenticationService } from './authentication.service';
import { ItemMenu, Tenant, User, UserTenant } from '../../shared/models';


@Injectable({
  providedIn: 'root'
})
export class AuthorizationService implements AuthorizationServiceInterface {

  private currentUser: User;
  private allUserPermissions: Array<string>;
  private allUserRoles: Array<string>;

  constructor(
    private eventsHubService: EventsHubService,
    private authenticationService: AuthenticationService
  ) {
    this.getCurrentUser();
    this.loggedInSubscription();
  }

  public isSuperAdmin(): boolean {
    return this.currentUser.superUser;
  }

  public isTenantAdmin(): boolean {
    return this.currentUser.userTenants.some(item => item.administrator);
  }

  public hasPermission(permissions: Array<string>, tenant?: Tenant, isLenient?: boolean): boolean {
    return this.isSuperAdmin() || this.isTenantAdmin() || this.checkPermissionList(permissions, isLenient);
  }

  public hasTenantRole(roles: Array<string>) {
    return this.isSuperAdmin() || this.isTenantAdmin() || this.checkRoleList(roles);
  }

  private checkPermissionList(requiredPermissions: Array<string>, isLenient?: boolean): boolean {
    const userPermissions: Array<string> = this.getAllUserPermissions();

    if (isLenient) {
      let hasAtLeastOnePermission = false;
      for (const permission of requiredPermissions) {
        if (userPermissions.includes(permission)) {
          hasAtLeastOnePermission = true;
        }
      }
      return hasAtLeastOnePermission;

    } else {
      let hasAllPermissions = true;
      for (const permission of requiredPermissions) {
        if (!userPermissions.includes(permission)) {
          hasAllPermissions = false;
        }
      }
      return hasAllPermissions;
    }
  }

  private checkRoleList(requiredRoles: Array<string>, ) {
    const userRoles: Array<string> = this.getAllUserRoles();
    let hasAllRoles = true;
      for (const role of requiredRoles) {
        if (!userRoles.includes(role)) {
          hasAllRoles = false;
        }
      }
      return hasAllRoles;
  }

  private getAllUserPermissions(): Array<string> {

    let userPermissions: Array<string> = [];

    if (!this.allUserPermissions) {

      for (const uTenant of this.currentUser.userTenants) {
        const tenantPermissions = this.getUserPermissionsForTenant(new Tenant({ id: uTenant.id }));
        userPermissions = userPermissions.concat(tenantPermissions);
      }
      this.allUserPermissions = Array.from(new Set(userPermissions)); // Remove Duplicates;
    }
    userPermissions = this.allUserPermissions;
    return userPermissions;
  }

  private getAllUserRoles(): Array<string> {
    let userRoles: Array<string> = [];

    if (!this.allUserRoles) {

      for (const uTenant of this.currentUser.userTenants) {
        const tenantPermissions = this.getUserRolesForTenant(new Tenant({ id: uTenant.id }));
        userRoles = userRoles.concat(tenantPermissions);
      }
      this.allUserRoles = Array.from(new Set(userRoles)); // Remove Duplicates;
    }
    userRoles = this.allUserRoles;
    return userRoles;

  }

  private getUserTenant(tenant: Tenant): UserTenant {
    let userTenant: UserTenant;

    for (const uTenant of this.currentUser.userTenants) {
      if (uTenant.id === tenant.id) {
        userTenant = new UserTenant(uTenant);
      }
    }

    return userTenant || new UserTenant();
  }

  private extractPermissionsFromUserTenant(userTenant: UserTenant): Array<string> {
    const userTenantPermissions: Array<string> = [];

    for (const role of userTenant.tenantRoles) {
      for (const permission of role.permissions) {
        userTenantPermissions.push(permission.code);
      }
    }

    return userTenantPermissions;
  }

  private extractRolesFromUserTenant(userTenant: UserTenant): Array<string> {
    const userTenantRoles: Array<string> = [];
    for (const role of userTenant.tenantRoles) {
      userTenantRoles.push(role.name);
    }
    return userTenantRoles;
  }

  private getUserPermissionsForTenant(tenant: Tenant): Array<string> {
    const userTenant: UserTenant = this.getUserTenant(tenant);
    const permissions: Array<string> = userTenant ? this.extractPermissionsFromUserTenant(userTenant) : [];

    return permissions;
  }

  private getUserRolesForTenant(tenant: Tenant): Array<string> {
    const userTenant: UserTenant = this.getUserTenant(tenant);
    const roles: Array<string> = userTenant ? this.extractRolesFromUserTenant(userTenant) : [];

    return roles;
  }

  public filterMenu(menu: Array<ItemMenu>, isFirstLevelItem?: boolean): Array<any> {
    return menu.filter(item =>
      this.hasPermission(item.permissionsNeeded, undefined, isFirstLevelItem) ||
      (item.permissionsLenient && this.hasPermission(item.permissionsLenient, undefined, true))
    );
  }

  private loggedInSubscription() {
    this.eventsHubService.isLoggedIn$
      .subscribe(
        (loggedIn: boolean) => {
          if (loggedIn) {
            this.getCurrentUser();
          }
        }
      );
  }

  private getCurrentUser() {
    this.currentUser = this.authenticationService.getLoggedUser();
  }

}
